import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, RefreshCw, AlertTriangle, CheckCircle2, Clock, Activity } from "lucide-react";

/** Single-glance health snapshot of the Companion / v3 print pipeline.
 *
 * Reads `/api/admin/print-health` every 15s and surfaces:
 *   • Today's throughput + success rate (success rate < 95% turns amber)
 *   • Status counts (pending/claimed/printed/failed)
 *   • Per-printer 24h breakdown (printed vs retry attempts)
 *   • Newest stuck job (retry ≥ 3) — leading indicator of a config issue
 *   • Problematic-products counter linking to the Settings audit
 *
 * Designed for the Admin page: read-only, no destructive actions.
 */
export default function PrintHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await http.get("/admin/print-health");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "fetch failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000); // 15s refresh — cheap aggregate
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <Card className="p-6 text-sm text-slate-500" data-testid="print-health-loading">
        Φόρτωση κατάστασης εκτυπώσεων…
      </Card>
    );
  }
  if (err && !data) {
    return (
      <Card className="p-6 text-sm text-red-700 bg-red-50 border-red-200" data-testid="print-health-error">
        Σφάλμα: {err}
      </Card>
    );
  }

  const s = data || {};
  const tp = s.throughput_24h || {};
  const sc = s.status_counts || {};
  const successPct = Number(tp.success_rate || 0);
  const successColor =
    successPct >= 95 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : successPct >= 80 ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-red-700 bg-red-50 border-red-200";

  return (
    <Card className="p-6 space-y-4" data-testid="print-health-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Printer size={18} /> Κατάσταση Εκτυπώσεων
          <span className="text-[10px] font-mono text-slate-400 ml-1">live · 15s</span>
        </h3>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="print-health-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          icon={<CheckCircle2 size={16} />}
          label="Τυπωμένα (24h)"
          value={tp.printed || 0}
          tone="emerald"
          testid="kpi-printed"
        />
        <Kpi
          icon={<Activity size={16} />}
          label="Επιτυχία"
          value={`${successPct.toFixed(1)}%`}
          tone={successPct >= 95 ? "emerald" : successPct >= 80 ? "amber" : "red"}
          testid="kpi-success"
        />
        <Kpi
          icon={<Clock size={16} />}
          label="Σε αναμονή"
          value={(sc.pending || 0) + (sc.claimed || 0)}
          tone={(sc.pending || 0) + (sc.claimed || 0) > 5 ? "amber" : "slate"}
          testid="kpi-pending"
        />
        <Kpi
          icon={<AlertTriangle size={16} />}
          label="Προβληματικά προϊόντα"
          value={s.problematic_products || 0}
          tone={(s.problematic_products || 0) > 0 ? "red" : "emerald"}
          testid="kpi-problematic"
        />
      </div>

      {/* Stuck job alert */}
      {s.stuck_job && (
        <div className="border border-red-300 bg-red-50 rounded-lg p-3 text-xs text-red-800" data-testid="print-health-stuck">
          <div className="font-bold flex items-center gap-1.5">
            <AlertTriangle size={14} /> Κολλημένο job
          </div>
          <div className="mt-1">
            «{s.stuck_job.file_name || s.stuck_job.kind}» στον <code>{s.stuck_job.windows_printer_name}</code>,
            {" "}retries={s.stuck_job.retry_count}, error: <em>{s.stuck_job.last_error || "—"}</em>
          </div>
        </div>
      )}

      {/* Per-printer breakdown */}
      {(s.per_printer_24h || []).length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1.5">Ανά εκτυπωτή (24h)</div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-3 py-1.5 font-semibold">Εκτυπωτής</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Τυπωμένα</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Σε αναμονή</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Retries</th>
                </tr>
              </thead>
              <tbody>
                {s.per_printer_24h.map((p) => (
                  <tr key={p.name} className="border-t" data-testid={`print-health-row-${p.name}`}>
                    <td className="px-3 py-1.5 font-mono">{p.name}</td>
                    <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">{p.printed}</td>
                    <td className="px-3 py-1.5 text-right text-amber-700">{p.pending || 0}</td>
                    <td className={`px-3 py-1.5 text-right ${p.retries > 0 ? "text-red-700 font-semibold" : "text-slate-400"}`}>
                      {p.retries || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status mini-legend */}
      <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
        <span>Συνολικά: </span>
        <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">printed {sc.printed || 0}</span>
        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">pending {sc.pending || 0}</span>
        <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">claimed {sc.claimed || 0}</span>
        <span className={`px-1.5 py-0.5 rounded ${successColor}`}>{tp.retry_attempts || 0} retry attempts</span>
      </div>
    </Card>
  );
}

function Kpi({ icon, label, value, tone = "slate", testid }) {
  const palette = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    red: "bg-red-50 border-red-200 text-red-900",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div className={`border rounded-lg px-3 py-2.5 ${palette}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium opacity-70">
        {icon} {label}
      </div>
      <div className="text-2xl font-heading font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
